<template>
  <div v-if="hasValidLocation && !isEditingLocation" class="location-flow">
    <Field
      type="hidden"
      :name="fieldName('search')"
      :model-value="searchFieldValue"
    />
    <div class="selected-location-summary">
      <div class="section-label selected-location-label">Ausgewählter Ort</div>
      <div class="selected-location-name">{{ selectedLocationLabel }}</div>
      <div v-if="selectedLocationAddress" class="selected-location-address">{{ selectedLocationAddress }}</div>
      <button type="button" class="btn btn-secondary btn-sm mt-3" @click="editLocation">
        Ändern
      </button>
    </div>
  </div>

  <div v-else class="row g-4 align-items-start">
    <div class="col-12 col-lg-8">
      <div class="location-flow">
        <section class="content-panel create-location-panel">
          <div class="panel-header">
            <h5 class="panel-title mb-1">Standort erfassen</h5>
            <p class="panel-description mb-0">
              Geben Sie die Hausadresse ein. Die Adresse wird beim Speichern geprüft.
            </p>
          </div>

          <div class="form-group geosearch-wrapper mb-3">
            <label class="form-label" :for="fieldName('search')">Adresse eingeben</label>
            <p class="helper-text">
              Geben Sie Straße, Hausnummer und Postleitzahl ein.
            </p>
            <template v-if="!isAddressPresent">
              <Field type="search" v-model="search" @keyup="getCoords" :id="fieldName('search')" ref="search" :name="fieldName('search')"
                     class="form-control"
                     :class="{'is-invalid': showSearchError}" autocomplete="off"
                     placeholder="Straße Hausnummer PLZ"
              >
              </Field>
            </template>
            <template v-else>
              <button type="button" class="btn btn-secondary btn-sm" @click="resetAddress">Adresse ändern
              </button>
            </template>
            <div v-if="showSearchError" class="invalid-feedback d-block">
              {{ fieldError('search') }}
            </div>
            <p v-if="showGeoSearchHint" class="helper-text mb-0">
              Geben Sie mindestens 4 Zeichen ein, um Adressvorschläge zu sehen.
            </p>
            <p v-else-if="showNoGeoResults" class="helper-text helper-text-strong mb-0">
              Keine passende Hausadresse gefunden. Bitte verfeinern Sie die Eingabe.
            </p>
            <div v-if="showDropdown" class="geo-results-section mt-3">
              <div class="section-label">Adressvorschläge</div>
              <div class="geo-results-list" role="list">
                <button
                  v-for="(item, index) in filteredGeoSearchResults"
                  :key="`${item.properties.osm_id}-${index}`"
                  type="button"
                  class="search-result-item geo-result-item"
                  @click="setAddress(index)"
                >
                  <span class="result-name">{{ featureTitle(item) }}</span>
                  <span class="result-address">{{ featureSubtitle(item) }}</span>
                  <span v-if="item.properties.name" class="result-meta">{{ item.properties.name }}</span>
                </button>
              </div>
            </div>
          </div>

          <div v-if="isAddressPresent" class="address-form">
            <div class="section-label">Angaben zum Standort</div>
            <div class="row g-3">
              <div class="col-12">
                <label :for="fieldName('address_name')" class="form-label">Name des Ortes</label>
                <Field ref="addressName" type="text" v-model="address.name" :id="fieldName('address_name')" :name="fieldName('address_name')" class="form-control"
                       :class="{'is-invalid': fieldError('address_name')}" autocomplete="off" placeholder="Name des Ortes"
                       aria-required="true" required
                >
                </Field>
                <div v-if="fieldError('address_name')" class="invalid-feedback d-block">
                  {{ fieldError('address_name') }}
                </div>
              </div>
              <div class="col-12 col-md-6">
                <div class="form-label">Straße</div>
                <div class="readonly-value">{{ address.street }}</div>
              </div>
              <div class="col-12 col-md-6">
                <div class="form-label">Postleitzahl / Ort</div>
                <div class="readonly-value">{{ formattedPostcodeCity }}</div>
              </div>
            </div>

            <div class="d-flex flex-wrap gap-2 mt-4">
              <button type="button" class="btn btn-primary" @click="confirmLocation">
                Standort übernehmen
              </button>
              <button v-if="previousAddress" type="button" class="btn btn-secondary" @click="cancelEditing">
                Abbrechen
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
    <div class="col-12 col-lg-4 map-column">
      <LeafletLocationMap
        title="Kartenansicht"
        description="Die Karte zeigt den aktuell ausgewählten oder neu erfassten Ort."
        :lat="markerLat"
        :lon="markerLon"
      />
    </div>
  </div>
</template>

<script>
import {useUserStore} from "@/stores/user";
import {Field, useFormContext} from 'vee-validate';
import axios from "axios"
import {
  buildPhotonSearchUrl,
  filterPhotonHouseResults,
  formatPhotonFeatureSubtitle,
  formatPhotonFeatureTitle,
  PHOTON_MIN_QUERY_LENGTH,
  PHOTON_SEARCH_DELAY,
  shouldSearchPhoton,
} from "@/scripts/locationSearch";
import LeafletLocationMap from "../survey/parts/LeafletLocationMap.vue";

export default {
  components: {
    Field,
    LeafletLocationMap,
  },
  setup() {
    const {setFieldValue, setFieldError, setFieldTouched} = useFormContext()
    return {
      userStore: useUserStore(),
      setFieldValue,
      setFieldError,
      setFieldTouched,
    }
  },
  data() {
    return {
      showDropdown: false,
      search: '',
      markerLat: null,
      markerLon: null,
      timer: null,
      searchResult: {
        features: []
      },
      address: {},
      isEditingLocation: !this.modelValue || !Object.keys(this.modelValue).length,
      previousAddress: null,
      prefetchedAddressClientId: null,
      addressClient: {
        own: this.addressClientId || null,
        other: null,
        search: '',
        searchResult: [],
        created: false,
        searching: false,
      },
    }
  },
  props: {
    errors: Object,
    modelValue: {
      type: Object,
      default: null,
    },
    addressClientId: {
      type: Number,
      default: null,
    },
    /** When true, use /clients/search for existing saved locations. Survey uses public locations API. */
    legacyClientSearch: {
      type: Boolean,
      default: false,
    },
    fieldPrefix: {
      type: String,
      default: '',
    },
  },
  computed: {
    searchFieldValue() {
      return this.selectedLocationLabel || this.addressClient.search || this.search || ''
    },
    isAddressPresent() {
      return Boolean(this.address?.street)
    },
    hasValidLocation() {
      return Boolean(this.address?.id) || (Boolean(this.address?.street) && this.hasNewLocationName)
    },
    hasNewLocationName() {
      return Boolean(String(this.address?.name || '').trim())
    },
    filteredGeoSearchResults() {
      return filterPhotonHouseResults(this.searchResult.features)
    },
    showGeoSearchHint() {
      return !this.isAddressPresent && this.search.length > 0 && this.search.length < PHOTON_MIN_QUERY_LENGTH
    },
    showNoGeoResults() {
      return !this.isAddressPresent && this.search.length >= PHOTON_MIN_QUERY_LENGTH && !this.showDropdown && this.filteredGeoSearchResults.length === 0
    },
    formattedPostcodeCity() {
      return [this.address.postcode || this.address.zip, this.address.city || this.address.town].filter(Boolean).join(' ')
    },
    selectedLocationLabel() {
      if (this.address?.name) {
        return this.address.name
      }

      if (this.address?.street) {
        return this.address.street
      }

      if (this.address?.address_string) {
        return this.address.address_string
      }

      return ''
    },
    selectedLocationAddress() {
      if (this.address?.address_string) {
        return this.address.address_string
      }

      if (this.address?.street || this.address?.postcode || this.address?.city || this.address?.zip || this.address?.town) {
        const postcode = this.address.postcode || this.address.zip
        const city = this.address.city || this.address.town
        return [this.address.street, [postcode, city].filter(Boolean).join(' ')].filter(Boolean).join(', ')
      }

      if (this.address?.town) {
        return this.address.town
      }

      return ''
    },
    showSearchError() {
      return Boolean(this.fieldError('search')) && !this.hasValidLocation
    },
  },
  methods: {
    applyCoordinatesFromAddress(address = this.address) {
      const lat = address?.latitude ?? address?.lat
      const lon = address?.longitude ?? address?.lon
      if (lat == null || lon == null || lat === '' || lon === '') {
        return false
      }

      const markerLat = Number(lat)
      const markerLon = Number(lon)
      if (!Number.isFinite(markerLat) || !Number.isFinite(markerLon)) {
        return false
      }

      this.markerLat = markerLat
      this.markerLon = markerLon
      return true
    },
    applyModelValueToLocalState(newValue) {
      this.address = { ...newValue }
      if (this.address.id != null && this.address.id !== '') {
        this.addressClient.other = this.address.id
        this.addressClient.search =
          this.address.name || this.address.address_string || String(this.address.id)
      }
      this.applyCoordinatesFromAddress()
      this.syncSearchFromAddress()
      this.isEditingLocation = false
      this.$nextTick(() => this.syncSearchValidation())
    },
    fieldName(name) {
      return this.fieldPrefix ? `${this.fieldPrefix}_${name}` : name
    },
    fieldError(name) {
      return this.errors?.[this.fieldName(name)]
    },
    useAddress() {
      this.$emit('update:modelValue', this.address)
    },
    featureTitle(feature) {
      return formatPhotonFeatureTitle(feature)
    },
    featureSubtitle(feature) {
      return formatPhotonFeatureSubtitle(feature)
    },
    setAddress(index, selectedFeature = null) {
      if (!selectedFeature) {
        selectedFeature = this.filteredGeoSearchResults[index]
      }

      this.address.id = null
      this.markerLat = selectedFeature.geometry.coordinates[1]
      this.markerLon = selectedFeature.geometry.coordinates[0]
      this.showDropdown = false
      this.address.name = selectedFeature.properties.name
      this.address.street = [selectedFeature.properties.street, selectedFeature.properties.housenumber].filter(v => v).join(' ')
      this.address.postcode = selectedFeature.properties.postcode
      this.address.city = selectedFeature.properties.city
      this.address.country = selectedFeature.properties.country
      this.address.lat = selectedFeature.geometry.coordinates[1]
      this.address.lon = selectedFeature.geometry.coordinates[0]
      this.syncSearchFromAddress()
      this.useAddress()
      this.$nextTick(() => this.validateNewLocationName())
    },
    editLocation() {
      this.previousAddress = {...this.address}
      this.resetAddress()
      this.isEditingLocation = true
    },
    confirmLocation() {
      if (!this.address.id && !this.isAddressPresent) {
        return
      }
      if (!this.address.id && !this.validateNewLocationName()) {
        return
      }

      this.useAddress()
      this.previousAddress = null
      this.isEditingLocation = false
      this.$emit('confirmed', this.address)
      this.$nextTick(() => this.syncSearchValidation())
    },
    cancelEditing() {
      if (!this.previousAddress) {
        return
      }

      this.applyModelValueToLocalState(this.previousAddress)
      this.useAddress()
      this.previousAddress = null
      this.isEditingLocation = false
    },
    async geocodeAddressIfNeeded() {
      if (this.markerLat != null && this.markerLon != null) {
        return
      }

      const query = [
        this.address.street,
        this.address.zip || this.address.postcode,
        this.address.town || this.address.city,
      ].filter(Boolean).join(' ')

      if (!shouldSearchPhoton(query)) {
        return
      }

      try {
        const {data} = await axios.get(buildPhotonSearchUrl(query))
        const results = filterPhotonHouseResults(data.features)
        if (!results.length) {
          return
        }

        const [lon, lat] = results[0].geometry.coordinates
        this.markerLat = lat
        this.markerLon = lon
      } catch (_) {
        // geocoding is best-effort for map preview only
      }
    },
    async ensureMapCoordinates() {
      if (this.applyCoordinatesFromAddress()) {
        return
      }

      if (this.addressClientId) {
        try {
          const locations = await this.userStore.fetchMyLocations()
          const matched = locations.find((loc) => String(loc.id) === String(this.addressClientId))
          if (matched?.latitude != null && matched?.longitude != null) {
            this.markerLat = Number(matched.latitude)
            this.markerLon = Number(matched.longitude)
            return
          }
        } catch (_) {
          // fetchMyLocations errors are handled centrally
        }
      }

      await this.geocodeAddressIfNeeded()
    },
    async prefillAddressClientSelection() {
      if (!this.addressClientId) {
        await this.ensureMapCoordinates()
        return
      }
      if (this.prefetchedAddressClientId === this.addressClientId || String(this.addressClient.other) === String(this.addressClientId)) {
        if (!this.applyCoordinatesFromAddress()) {
          await this.ensureMapCoordinates()
        }
        return
      }

      this.prefetchedAddressClientId = this.addressClientId
      this.addressClient.search = String(this.addressClientId)

      try {
        const results = this.legacyClientSearch
          ? await this.userStore.searchClients(this.addressClient, {force: true})
          : await this.userStore.searchLocations(this.addressClient, {force: true})
        const matchedClient = results.find((client) => String(client.id) === String(this.addressClientId))

        if (!matchedClient) {
          await this.ensureMapCoordinates()
          return
        }

        this.addressClient.search = matchedClient.name || matchedClient.address_string || String(this.addressClientId)
        this.addressClient.other = matchedClient.id
        this.addressChanged()
        if (this.markerLat == null || this.markerLon == null) {
          await this.ensureMapCoordinates()
        }
      } catch (_) {
        await this.ensureMapCoordinates()
      }
    },
    addressChanged() {
      if (this.addressClient.other) {
        const clientAddressData = this.addressClient.searchResult.find((sr) => String(sr.id) === String(this.addressClient.other))
        if (!clientAddressData) {
          this.address = {}
          this.markerLat = null
          this.markerLon = null
          return
        }
        this.address.id = this.addressClient.other;
        this.address.name = clientAddressData.name;
        this.address.address_string = clientAddressData.address_string;
        this.address.town = clientAddressData.town;
        if (!this.applyCoordinatesFromAddress({
          ...this.address,
          latitude: clientAddressData.latitude ?? clientAddressData.lat,
          longitude: clientAddressData.longitude ?? clientAddressData.lon,
        })) {
          this.markerLat = null
          this.markerLon = null
        }
        this.syncSearchFromAddress()
        this.useAddress()
        this.$nextTick(() => this.syncSearchValidation())
      } else {
        this.address = {}
        this.markerLat = null
        this.markerLon = null
        this.useAddress()
      }
    },
    validateNewLocationName() {
      const field = this.fieldName('address_name')
      if (this.hasNewLocationName) {
        this.setFieldError(field, undefined)
        return true
      }

      this.setFieldTouched(field, true)
      this.setFieldError(field, 'Bitte geben Sie einen Namen für den Ort ein.')
      this.$nextTick(() => {
        if (this.$refs.addressName) {
          this.$refs.addressName.$el.focus()
        }
      })
      return false
    },
    syncSearchFromAddress() {
      this.search = this.searchFieldValue
    },
    clearSearchValidation() {
      const fullName = this.fieldName('search')
      this.setFieldValue(fullName, '', false)
      this.setFieldError(fullName, undefined)
      this.setFieldTouched(fullName, false)
    },
    syncSearchValidation() {
      if (!this.hasValidLocation) {
        return
      }

      const field = this.fieldName('search')
      this.setFieldValue(field, this.searchFieldValue, true)
      this.setFieldError(field, undefined)
    },
    resetAddress() {
      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
      }

      this.address = {};
      this.search = ''
      this.searchResult = {features: []}
      this.addressClient.other = null
      this.showDropdown = false
      this.markerLat = null
      this.markerLon = null
      this.clearSearchValidation()
      this.$nextTick(() => this.focusSearch());
    },
    getCoords() {
      if (this.timer) {
        clearTimeout(this.timer)
      }

      if (!shouldSearchPhoton(this.search)) {
        this.searchResult = {features: []}
        this.showDropdown = false
        return
      }

      this.timer = setTimeout(() => {
        const query = this.search.trim()
        if (!shouldSearchPhoton(query)) {
          return
        }

        axios.get(buildPhotonSearchUrl(query)).then(({data}) => {
          this.searchResult = data
          this.showDropdown = this.filteredGeoSearchResults.length > 0
          this.focusSearch();
        })
      }, PHOTON_SEARCH_DELAY);
    },
    focusSearch() {
      if (this.$refs.search) {
        this.$refs.search.$el.focus()
        // console.log("focus!!!!!!!!!")
      }
    },
  },
  beforeUnmount() {
    if (this.timer) {
      clearTimeout(this.timer)
    }
  },
  mounted() {
    if (this.modelValue && Object.keys(this.modelValue).length) {
      this.applyModelValueToLocalState(this.modelValue)
    } else {
      this.$nextTick(() => this.clearSearchValidation())
    }
    this.prefillAddressClientSelection()
  },

  watch: {
    modelValue(newValue) {
      if (!newValue || !Object.keys(newValue).length) {
        return
      }
      if (this.isEditingLocation) {
        return
      }
      this.applyModelValueToLocalState(newValue)
      this.ensureMapCoordinates()
    },
    'address.name'() {
      if (!this.isAddressPresent) {
        return
      }

      if (this.hasNewLocationName) {
        this.setFieldError(this.fieldName('address_name'), undefined)
      } else if (!this.address.id) {
        this.setFieldValue(this.fieldName('search'), '', false)
        this.setFieldError(this.fieldName('address_name'), 'Bitte geben Sie einen Namen für den Ort ein.')
      }
      this.syncSearchFromAddress()
      this.$nextTick(() => this.syncSearchValidation())
    },
    addressClientId() {
      this.prefillAddressClientSelection()
    }
  }
}
</script>

<style scoped lang="scss">
@import "src/scss/constants";

.location-flow {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .content-panel {
    border: 1px solid #dee2e6;
    background: #ffffff;
    padding: 1rem;
  }

  .panel-header {
    margin-bottom: 1rem;
  }

  .panel-title {
    font-size: 1rem;
    font-weight: 600;
    color: #212529;
  }

  .panel-description,
  .helper-text {
    color: #6c757d;
    font-size: 0.95rem;
    line-height: 1.45;
  }

  .helper-text {
    margin-top: 0.5rem;
  }

  .helper-text-strong {
    color: #495057;
  }

  .section-label {
    margin-bottom: 0.75rem;
    color: #6c757d;
    font-size: 0.85rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .create-location-panel {
    .helper-text {
      max-width: 48rem;
    }
  }

  .geo-results-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
  }

  .search-result-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.2rem;
    width: 100%;
    padding: 0.9rem 1rem;
    text-align: left;
    border: 1px solid #dee2e6;
    background-color: #fff;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

    &:hover,
    &:focus {
      border-color: $manet_blue_dark;
      box-shadow: 0 0 0 0.2rem rgba(4, 53, 94, 0.12);
      transform: translateY(-1px);
      outline: none;
    }

    .result-name {
      font-weight: 600;
      color: #212529;
    }

    .result-address {
      color: #6c757d;
      font-size: 0.95rem;
    }

    .result-meta {
      color: #495057;
      font-size: 0.85rem;
    }
  }

  .geo-result-item {
    gap: 0.35rem;
  }

  .readonly-value {
    color: #495057;
    font-size: 1rem;
    line-height: 1.5;
    overflow-wrap: anywhere;
  }

  .selected-location-summary {
    position: relative;
    overflow: hidden;
    border: 1px solid rgba(31, 122, 85, 0.24);
    border-left: 4px solid #1f7a55;
    background: #eef8f3;
    box-shadow: 0 0.5rem 1.25rem rgba(31, 122, 85, 0.09);
    padding: 1.15rem 1.25rem;

    .selected-location-label {
      display: inline-flex;
      align-items: center;
      gap: 0.45rem;
      margin-bottom: 0.65rem;
      padding: 0.3rem 0.65rem 0.3rem 0.4rem;
      background: rgba(31, 122, 85, 0.11);
      color: #176442;
      letter-spacing: 0.045em;

      &::before {
        content: "\2713";
        display: inline-grid;
        width: 1.35rem;
        height: 1.35rem;
        place-items: center;
        border-radius: 50%;
        background: #1f7a55;
        color: #ffffff;
        font-size: 0.78rem;
        line-height: 1;
      }
    }

    .selected-location-name {
      color: #176442;
      font-size: 1.05rem;
      font-weight: 700;
    }

    .selected-location-address {
      color: #6c757d;
      margin-top: 0.2rem;
    }
  }

  .helper-box {
    border: 1px solid #dee2e6;
    background: #f8f9fa;
    padding: 1rem;
    color: #495057;
  }
}

@media (max-width: 991.98px) {
  .location-flow {
    .geo-results-list {
      grid-template-columns: 1fr;
    }
  }
}

@media (min-width: 992px) {
  .map-column {
    align-self: stretch;
  }
}

</style>
